import { belongsTo, attr } from '@ember-data/model';
import ShModel from './sh-model';
import { computed } from '@ember/object';
import { getAccountMethodIcon } from '../constants/payout';
import { PAYOUT_METHOD } from './payout-method';

const ACCOUNT_TYPES = {
  BANK: 'bank',
  CUSTOM: 'custom',
  HYPERWALLET: 'hyperwallet',
  PAYONEER: 'payoneer',
  PAYPAL: 'paypal',
};

const STATUSES = {
  UNLINKED: -2,
  DECLINED: -1,
  INACTIVE: 0,
  PENDING: 0.5,
  ACTIVE: 1,
};

const STATUS_NAMES = {
  '-2': 'Unlinked',
  '-1': 'Declined',
  '0': 'Inactive',
  '0.5': 'Pending',
  '1': 'Active',
};

const REQUIRED_ACTIONS = {
  NONE: 'none',
  SETUP: 'setup_incomplete',
  VERIFICATION: 'verification',
  VERIFICATION_PENDING: 'verification_pending',
};

const getAccountTypeText = (accountType, accountMethod) => {
  switch (accountType) {
    case PAYOUT_ACCOUNT.ACCOUNT_TYPES.CUSTOM:
      return PAYOUT_METHOD.CUSTOM_ACCOUNT_METHOD_NAMES[accountMethod] ?? '';
    case PAYOUT_ACCOUNT.ACCOUNT_TYPES.BANK:
      return 'Bank';
    case PAYOUT_ACCOUNT.ACCOUNT_TYPES.HYPERWALLET:
      return PAYOUT_METHOD.HYPERWALLET_ACCOUNT_METHOD_NAMES[accountMethod] ?? '';
    case PAYOUT_ACCOUNT.ACCOUNT_TYPES.PAYONEER:
      return 'Payoneer';
    case PAYOUT_ACCOUNT.ACCOUNT_TYPES.PAYPAL:
      return 'PayPal';
    default:
      return '';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case PAYOUT_ACCOUNT.STATUSES.UNLINKED:
      return 'lsg-dark-gray';
    case PAYOUT_ACCOUNT.STATUSES.DECLINED:
      return 'lsg-black';
    case PAYOUT_ACCOUNT.STATUSES.INACTIVE:
      return 'lsg-yellow';
    case PAYOUT_ACCOUNT.STATUSES.PENDING:
      return 'lsg-yellow';
    case PAYOUT_ACCOUNT.STATUSES.ACTIVE:
      return 'lsg-green';
    default:
      return 'lsg-black';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case PAYOUT_ACCOUNT.STATUSES.UNLINKED:
      return 'Unlinked';
    case PAYOUT_ACCOUNT.STATUSES.DECLINED:
      return 'Declined';
    case PAYOUT_ACCOUNT.STATUSES.INACTIVE:
      return 'Inactive';
    case PAYOUT_ACCOUNT.STATUSES.PENDING:
      return 'Pending';
    case PAYOUT_ACCOUNT.STATUSES.ACTIVE:
      return 'Active';
    default:
      return '';
  }
};

export const PAYOUT_ACCOUNT = {
  ACCOUNT_TYPES,
  STATUSES,
  STATUS_NAMES,
  REQUIRED_ACTIONS,
  getAccountTypeText,
  getStatusColor,
  getStatusText,
};

const PayoutAccount = ShModel.extend({
  accountType: attr('string'),
  accountMethod: attr('string'),
  accountLabel: attr('string'),
  currency: attr('string'),
  createdAt: attr('date'),
  loginLink: attr('string'),
  providerId: attr('string'),
  registrationLink: attr('string'),
  status: attr('number'),
  requiredAction: attr('string'),
  preferred: attr('boolean'),
  updatedAt: attr('date'),

  // relationships
  user: belongsTo('user', { async: false, inverse: 'payoutAccounts' }),
  userPreferredAccount: belongsTo('user', { async: false, inverse: 'preferredPayoutAccount' }),

  accountTypeText: computed('accountType', 'accountMethod', function () {
    return PAYOUT_ACCOUNT.getAccountTypeText(this.get('accountType'), this.get('accountMethod'));
  }),

  statusColor: computed('status', function () {
    return PAYOUT_ACCOUNT.getStatusColor(this.get('status'));
  }),

  statusText: computed('status', function () {
    return PAYOUT_ACCOUNT.getStatusText(this.get('status'));
  }),

  /** @type {String} */
  accountMethodIcon: computed('accountType', 'accountMethod', function () {
    return getAccountMethodIcon(this.get('accountType'), this.get('accountMethod'));
  }),
});

export default PayoutAccount;
